import {twJoin} from 'tailwind-merge';
import {m as motion} from 'motion/react';
import {useEffect, useState} from 'react';
import NumberFlow from '@number-flow/react';
import {IconHeartShare, IconReload} from '@tabler/icons-react';
import VisitedCountriesBanner from '../common/VisitedCountriesBanner';
import getRandomInteger from 'server/utils/getRandomInteger';
import BucketListBanner from '../common/BucketListBanner';
import PlaceCard, {PlaceCardsGrid} from '../PlaceCard';
import SectionHeading from '../SectionHeading';
import useSession from 'src/hooks/useSession';
import BaseButton from '../BaseButton';
import ShareSheet from '../ShareSheet';
import QuizNavBar from './QuizNavBar';
import Dropdown from '../Dropdown';
import Footer from '../Footer';

type ResultPhrase = {
  title: string;
  description: string;
};

const resultPhrases = {
  terrible: [
    {title: 'You gave it a shot…', description: 'And you missed completely.'},
    {title: 'Not all who wander are lost.', description: "Except you. You're very, very lost."},
    {title: 'Your score is unique.', description: 'And by unique, we mean impressively low.'}
  ],
  bad: [
    {title: 'You tried!', description: 'Just not very hard, it seems.'},
    {title: 'Not terrible, but not good either.', description: 'You exist in quiz purgatory.'},
    {title: "You've got potential.", description: 'Potential to try again and do better.'},
    {title: 'You aimed for the stars…', description: 'And hit a lamp post.'},
    {title: 'Better luck next time!', description: 'Or maybe just better answers.'},
    {title: "It's not a total disaster.", description: 'Just mostly a disaster.'}
  ],
  ok: [
    {title: 'You are OK.', description: "These quizzes are not easy. At least for you they're not."},
    {title: 'A solid effort.', description: "You're cruising in the middle lane."},
    {title: "You're halfway there.", description: "That's half more than some people."},
    {title: 'Good-ish job.', description: 'Not a travel expert, but not lost either.'}
  ],
  great: [
    {title: 'A human compass.', description: 'Always pointing to correct.'},
    {title: 'Outstanding.', description: 'You could guide tourists and charge them for it.'},
    {title: 'You know your stuff.', description: 'Or you have suspiciously good guessing skills.'},
    {title: "You've clearly been places.", description: 'Or just stared at maps for fun, maybe.'}
  ],
  perfect: [
    {title: 'Flawless.', description: 'Yes you are, flawless.'},
    {title: 'Nailed it.', description: 'Please allow us to bow in admiration of your wisdom.'},
    {title: 'Geography GOAT.', description: 'Greatest of all travelers.'},
    {title: 'The quiz has been conquered.', description: 'Lord of the Maps.'}
  ]
} as Record<string, ResultPhrase[]>;

const getResultType = (successRate: number) => {
  if (successRate === 1) return 'perfect';
  if (successRate >= 0.7) return 'great';
  if (successRate >= 0.4) return 'ok';
  if (successRate >= 0.2) return 'bad';

  return 'terrible';
};

const getResultPhrase = (successRate: number) => {
  const resultType = getResultType(successRate);
  const phrases = resultPhrases[resultType];

  return phrases[getRandomInteger(0, phrases.length - 1)];
};

const getNumberOfCorrectAnswers = (quiz: QuizWithAuthorAndPlaces, givenAnswers: Record<string, string>) => {
  return quiz.questions.filter((question) => givenAnswers[question.id] === question.answers[0]).length;
};

type Props = {
  quiz: QuizWithAuthorAndPlaces;
  givenAnswers: Record<string, string>;
  playAgain: () => void;
  ref?: React.RefObject<HTMLDivElement>;
};

const QuizFinalPageContent = ({quiz, givenAnswers, playAgain, ref}: Props) => {
  const {session} = useSession();

  const totalQuestions = quiz.questions.length;

  const [resultPhrase] = useState<ResultPhrase>(
    getResultPhrase(getNumberOfCorrectAnswers(quiz, givenAnswers) / totalQuestions)
  );

  const [numberOfCorrectAnswers, setNumberOfCorrectAnswers] = useState(0);

  const places = quiz.questions.map((question) => question.place).filter(Boolean);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setNumberOfCorrectAnswers(getNumberOfCorrectAnswers(quiz, givenAnswers));
    }, 600);

    return () => clearTimeout(timeout);
  }, []);

  const delayStep = 0.5 / quiz.questions.length;

  return (
    <motion.div
      ref={ref}
      initial={{opacity: 0}}
      animate={{opacity: 1}}
      exit={{opacity: 0}}
      className="flex flex-col items-center w-full min-h-screen relative"
    >
      <QuizNavBar className="bg-transparent dark:bg-transparent" />

      <div className="flex flex-col w-full max-w-[1600px] px-(--page-padding) my-4 max-[700px]:my-0 gap-14">
        <div className="flex flex-col items-center justify-center h-[calc(100vh-360px)] max-h-[800px]">
          <h1 className="text-lg font-medium font-heading text-(--quiz-text-color) max-w-[320px] text-center leading-tight mb-8 starting:opacity-0 opacity-60 duration-[2s]">
            {quiz.name}
          </h1>

          <div className="relative rounded-full p-8 max-[700px]:p-6 max-[400px]:p-4 bg-[rgba(255,255,255,0.3)] dark:bg-[rgba(255,255,255,0.03)] transition-colors duration-1000 shadow-[inset_0_0_20px_rgba(255,255,255,0.1),inset_0_0_1px_rgba(255,255,255,0.4)]">
            <div className="relative w-[320px] max-[700px]:w-[240px] max-[400px]:w-[200px] aspect-square flex items-center justify-center">
              {quiz.questions?.map((question, index) => {
                const segmentAngle = -90 + (180 * index) / (quiz.questions.length - 1);
                const isCorrect = givenAnswers[question.id] === question.answers[0];

                return (
                  <div
                    key={question.id}
                    className="absolute top-0 left-1/2 w-2 max-[400px]:w-[6px] h-[160px] max-[700px]:h-[120px] max-[400px]:h-[100px] origin-bottom"
                    style={{transform: `translateX(-50%) rotate(${segmentAngle}deg)`}}
                  >
                    <div
                      style={{transitionDelay: `${index * delayStep + 0.5}s`}}
                      className={twJoin(
                        'absolute top-0 left-0 w-full h-12 max-[700px]:h-8 rounded-full starting:bg-transparent starting:!shadow-[0_0_2px_1px_rgba(255,255,255,0.1)] starting:dark:shadow-none starting:dark:bg-[rgba(0,0,0,0.5)] duration-300',
                        isCorrect
                          ? 'bg-[rgb(2,166,86)] shadow-[0_0_2px_1px_rgba(255,255,255,0.4),0_2px_32px_4px_rgba(34,197,94,0.6)] dark:shadow-[0_-1px_2px_2px_rgba(44,207,104,0.3),0_2px_40px_4px_rgba(34,197,94,0.7)] dark:bg-[rgba(34,197,94,1)]'
                          : 'bg-[rgba(239,68,68,1)] shadow-[0_0_2px_1px_rgba(255,255,255,0.4),0_2px_32px_4px_rgba(239,68,68,0.6)] dark:shadow-[0_-1px_2px_2px_rgba(239,68,68,0.3),0_2px_40px_4px_rgba(239,68,68,0.6)]'
                      )}
                    />
                  </div>
                );
              })}

              <div className="text-center text-(--quiz-text-color) max-[400px]:mt-2">
                <div className="text-8xl max-[700px]:text-6xl max-[400px]:text-5xl font-bold -my-7 max-[700px]:-my-4 max-[400px]:-my-3">
                  <NumberFlow value={numberOfCorrectAnswers} isolate />
                </div>
                <div className="text-2xl max-[700px]:text-xl max-[400px]:text-lg">of {totalQuestions}</div>
              </div>
            </div>

            <div className="absolute bottom-0 left-0 w-full h-[220px] max-[700px]:h-[160px] max-[400px]:h-[120px] bg-linear-to-t to-100% from-50% from-(--quiz-bg-color) to-transparent transition duration-1000 pointer-events-none" />
          </div>

          <div className="relative text-center items-center -mt-24 max-[700px]:-mt-20 max-[400px]:-mt-16 flex flex-col gap-3 max-[700px]:gap-2 text-(--quiz-text-color) starting:opacity-0 opacity-100 duration-[2s] delay-[1s]">
            <h2 className="text-4xl max-[700px]:text-3xl max-[400px]:text-2xl">{resultPhrase.title}</h2>
            <p className="text-lg/snug max-w-[340px] max-[400px]:text-base/snug opacity-60">
              {resultPhrase.description}
            </p>

            <div className="flex items-center mt-8 max-[400px]:mt-6 gap-4">
              <Dropdown items={[{component: <ShareSheet key={0} path={`/quiz/${quiz.nameId}`} />}]}>
                <BaseButton
                  buttonVariant="primary"
                  size="large"
                  label="Share the quiz"
                  TablerIcon={IconHeartShare}
                  iconSize={22}
                  className="[&_.h-icon]:-mt-1"
                />
              </Dropdown>

              <BaseButton
                buttonVariant="secondary"
                size="large"
                TablerIcon={IconReload}
                iconSize={22}
                className="w-11"
                onPress={playAgain}
              />
            </div>
          </div>
        </div>

        <div className="flex gap-16 starting:opacity-0 opacity-100 duration-[2s] delay-[1.5s]">
          {/* <div className="flex flex-col gap-3 w-0 max-w-[440px] grow self-stretch mt-4">
            <h3 className="text-lg font-medium font-heading text-(--quiz-text-color) opacity-60 ml-8">
              Recent scores
            </h3>

            <QuizSubmissions quizId={quiz._id} />
          </div> */}

          {places.length ? (
            <div className="flex flex-col gap-10 grow w-0 text-(--quiz-text-color)">
              <div className="flex flex-col">
                <SectionHeading className="mb-3 max-sm:mb-2">Explore the places from the quiz</SectionHeading>

                <p className="opacity-60">Save places you want to visit to your bucket list.</p>
              </div>

              <PlaceCardsGrid columns={5}>
                {places.map((place, index) => (
                  <PlaceCard key={place._id} place={place} priority={index <= 10} session={session} />
                ))}
              </PlaceCardsGrid>
            </div>
          ) : null}
        </div>

        {session ? (
          <div className="h-10" />
        ) : (
          <div className="pt-6 pb-16 flex max-lg:flex-col gap-6 w-full">
            <VisitedCountriesBanner />
            <BucketListBanner />
          </div>
        )}
      </div>

      <Footer />
    </motion.div>
  );
};

export default QuizFinalPageContent;
